"use client";

import { AnimatePresence, motion } from "framer-motion";
import Cookies from "js-cookie";
import { Check, Globe } from "lucide-react";
import { useLocale } from "next-intl";
import { usePathname, useSearchParams } from "next/navigation";
import { useEffect, useRef, useState, useTransition } from "react";
import { createPortal } from "react-dom";

type Locale = "en" | "ar";

const LANGUAGES: { code: Locale; label: string }[] = [
  { code: "en", label: "English" },
  { code: "ar", label: "العربية" },
];

interface LanguageSwitcherProps {
  variant?: "hero" | "scrolled";
}

export default function LanguageSwitcher({}: LanguageSwitcherProps) {
  const locale = useLocale() as Locale;
  const pathname = usePathname();
  const searchParams = useSearchParams();

  const [open, setOpen] = useState(false);
  const [isPending] = useTransition();
  const ref = useRef<HTMLUListElement>(null);
  const btnRef = useRef<HTMLButtonElement>(null);
  const [redirectTo, setRedirectTo] = useState<string | null>(null);
  const [dropdownStyle, setDropdownStyle] = useState<React.CSSProperties>({});

  // Navigate when locale changes
  useEffect(() => {
    if (redirectTo) {
      window.location.href = redirectTo;
    }
  }, [redirectTo]);

  // Close on outside click (checks both the dropdown ul and the trigger button)
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      const target = e.target as Node;
      const insideDropdown = ref.current?.contains(target);
      const insideBtn = btnRef.current?.contains(target);
      if (!insideDropdown && !insideBtn) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // Compute anchored position from button rect so the portal renders correctly.
  // Anchor by `left` and clamp within the viewport so it never overflows either
  // edge — this is what breaks in RTL (Arabic) on mobile, where the button sits
  // near the left edge and right-anchoring pushes the menu off-screen.
  const handleOpen = () => {
    if (!open && btnRef.current) {
      const rect = btnRef.current.getBoundingClientRect();
      const width = 180;
      const margin = 8;
      // default: align the menu's right edge with the button's right edge
      let left = rect.right - width;
      // keep it fully on-screen in both directions
      left = Math.max(margin, Math.min(left, window.innerWidth - width - margin));
      setDropdownStyle({
        position: "fixed",
        top: rect.bottom + 8,
        left,
        width,
        zIndex: 9999,
      });
    }
    setOpen((prev) => !prev);
  };

  const switchLocale = (nextLocale: Locale) => {
    if (nextLocale === locale) return;
    Cookies.set("DIRIYAH_NEXT_LOCALE", nextLocale, { path: "/", expires: 30 });

    const segments = pathname.split("/");
    segments[1] = nextLocale;

    const newPath =
      segments.join("/") +
      (searchParams.toString() ? `?${searchParams.toString()}` : "");

    setOpen(false);
    setRedirectTo(newPath);
  };

  const dropdown = (
    <AnimatePresence>
      {open && (
        <motion.ul
          ref={ref}
          initial={{ opacity: 0, y: -6, scale: 0.96 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: -6, scale: 0.96 }}
          transition={{ duration: 0.15 }}
          style={dropdownStyle}
          className="overflow-hidden rounded-2xl border border-gray-100 dark:border-white/10 bg-white dark:bg-zinc-900 shadow-xl"
        >
          {LANGUAGES.map((lang) => {
            const active = locale === lang.code;
            return (
              <li
                key={lang.code}
                onClick={() => switchLocale(lang.code)}
                className={`flex cursor-pointer items-center justify-between px-4 py-3 text-sm font-medium transition ${
                  active
                    ? "bg-brand-primary/10 text-brand-primary"
                    : "text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-white/10"
                }`}
              >
                <span>{lang.label}</span>
                {active && (
                  <motion.span
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    className="text-brand-primary"
                  >
                    <Check className="h-4 w-4" />
                  </motion.span>
                )}
              </li>
            );
          })}
        </motion.ul>
      )}
    </AnimatePresence>
  );

  return (
    <div className="relative select-none mt-1">
      <button
        ref={btnRef}
        onClick={handleOpen}
        disabled={isPending}
        className="flex items-center gap-2 rounded-full backdrop-blur-md transition disabled:opacity-50 cursor-pointer"
      >
        <Globe className="h-4 w-4" />
      </button>

      {typeof window !== "undefined"
        ? createPortal(dropdown, document.body)
        : dropdown}
    </div>
  );
}
